import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
import _IconFullscreen from "@arco-design/web-react/icon/react-icon/IconFullscreen";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import _IconFullscreenExit from "@arco-design/web-react/icon/react-icon/IconFullscreenExit";
import "@arco-design/web-react/es/Space/style";
import _Space from "@arco-design/web-react/es/Space";
import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from 'react';
import Modeler from 'bpmn-js/lib/Modeler';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-codes.css';
import { useRequest } from 'ahooks';
import { getFlowDefinitionXml, getFlowProgress } from '@/services';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import './index.less';
const STATUS_COLORS = {
  completed: {
    stroke: '#71c54b',
    fill: '#f1f9ed'
  },
  current: {
    stroke: '#59a0fe',
    fill: '#dbe8f7'
  },
  rejected: {
    stroke: '#f5222d',
    fill: '#fef0f0'
  }
};

/**
 * 把 xml 顶部所有**非** `<?xml ... ?>` 的处理指令（PI）剥掉。
 * 后端偶尔会在 `<?xml ?>` 之前塞 `<?flow-meta ... ?>` 等自定义 PI，
 * bpmn-js 严格要求从 `<?xml ?>` 开始解析，否则报 "no process or collaboration to display"。
 */
function stripNonXmlProcessingInstructions(xml) {
  if (!xml) return xml;
  return xml.replace(/<\?(?!xml\b)[^>]*\?>/gi, '');
}
function BpmnViewerComponent({
  id,
  processCode,
  processVersion,
  style
}) {
  const containerRef = useRef(null);
  const fullscreenRef = useRef(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const viewerRef = useRef(null);
  const [internalProgress, setInternalProgress] = useState([]);
  const [internalEdges, setInternalEdges] = useState([]);
  // 父组件没传 xml 时，自己去后端拉（接口 GET /flow/definition/:id/xml）。
  const [fetchedXml, setFetchedXml] = useState('');
  // 当前缩放百分比（0 表示尚未完成 fit-viewport）
  const [zoom, setZoom] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const progressApi = useRequest(getFlowProgress, {
    manual: true,
    onSuccess: res => {
      const items = res.nodes.map(node => ({
        id: node.id,
        status: node.status
      }));
      setInternalProgress(items);
      setInternalEdges(res.edges ?? []);
    }
  });

  // xml 没传 → 根据 id 拉后端 xml 接口
  const xmlApi = useRequest(args => getFlowDefinitionXml(args.id, args.version === -1 ? void 0 : args.version), {
    manual: true,
    onSuccess: data => {
      setFetchedXml(typeof data === 'string' ? data : '');
    },
    onError: e => {
      // eslint-disable-next-line no-console
      console.error('[BpmnViewer] 拉取 xml 失败', e);
    }
  });
  const loading = xmlApi.loading || progressApi.loading;
  useEffect(() => {
    if (!id) return;
    progressApi.run(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 按 id 拉后端 xml（不再支持 props 直传 xml）
  useEffect(() => {
    if (!processCode || !processVersion) return;
    xmlApi.run({
      id: processCode,
      version: processVersion
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processCode, processVersion]);
  useEffect(() => {
    if (!containerRef.current) return;
    const viewer = new Modeler({
      container: containerRef.current,
      additionalModules: [{
        paletteProvider: ['value', '']
      }, {
        labelEditingProvider: ['value', '']
      }, {
        contextPadProvider: ['value', '']
      }, {
        bendpoints: ['value', {}]
      }, {
        zoomScroll: ['value', '']
      }, {
        moveCanvas: ['value', '']
      }, {
        move: ['value', '']
      }]
    });
    viewerRef.current = viewer;
    if (!fetchedXml) {
      // xml 还没拉到，nothing to render；等 fetchedXml 变化后再触发一次 effect
      viewer.destroy();
      viewerRef.current = null;
      return;
    }

    // 后端有时会在 xml 顶部塞自定义处理指令（如 `<?flow-meta ...?>`），
    // bpmn-js 严格要求从 `<?xml ...?>` 开始解析，前置 PI 会让它报
    // "no process or collaboration to display"。这里剥掉所有非 `<?xml` 开头的 PI。
    const cleanedXml = stripNonXmlProcessingInstructions(fetchedXml);
    viewer.importXML(cleanedXml).then(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const canvas = viewer.get('canvas');
      canvas.zoom('fit-viewport', 'auto');
      applyColors(viewer, internalProgress, internalEdges);
      // 同步当前缩放比例，后续 zoom 变化时按百分比重新设置
      setZoom(canvas.zoom() * 100);
    }).catch(err => {
      console.error('BPMN 渲染失败:', err);
    });
    const palette = document.querySelector('.djs-palette');
    const poweredBy = document.querySelector('.bjs-powered-by');
    palette?.remove();
    poweredBy?.remove();
    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedXml]);
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    applyColors(viewer, internalProgress, internalEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalProgress, internalEdges]);

  // zoom 变化（由 fit-viewport 初始化或外部控制）时同步画布缩放
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !zoom) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const canvas = viewer.get('canvas');
    canvas.zoom(zoom / 100);
  }, [zoom]);

  // 全屏时把当前流程图 svg 克隆到全屏容器，配合 TransformWrapper 缩放/拖动
  useEffect(() => {
    if (isFullscreen && containerRef.current && fullscreenRef.current) {
      const svg = containerRef.current.querySelector('svg');
      if (svg) {
        const clone = svg.cloneNode(true);
        clone.style.width = `${svg.clientWidth}px`;
        clone.style.height = `${svg.clientHeight}px`;
        fullscreenRef.current.innerHTML = '';
        fullscreenRef.current.appendChild(clone);
      }
    } else if (!isFullscreen && fullscreenRef.current) {
      fullscreenRef.current.innerHTML = '';
    }
  }, [isFullscreen]);
  function title() {
    return /*#__PURE__*/React.createElement(_Space, {
      className: "flex-1"
    }, /*#__PURE__*/React.createElement("div", {
      className: "lx-flow-status"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null, "\u8FDB\u884C\u4E2D")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null, "\u5DF2\u901A\u8FC7")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null, "\u672A\u5B8C\u6210"))));
  }
  return /*#__PURE__*/React.createElement(_Spin, {
    className: "lx-spin-history lx-spin-bpmn",
    loading: loading
  }, /*#__PURE__*/React.createElement("div", {
    className: isFullscreen ? 'lx-flow-view__fullscreen' : ''
  }, /*#__PURE__*/React.createElement(TransformWrapper, {
    key: isFullscreen ? 'fullscreen' : 'normal',
    initialScale: isFullscreen ? 1.5 : 1,
    limitToBounds: true,
    doubleClick: {
      mode: 'reset',
      animationTime: 100,
      step: 0.5
    }
  }, /*#__PURE__*/React.createElement(TransformComponent, null, /*#__PURE__*/React.createElement("div", {
    ref: fullscreenRef,
    style: {
      width: '100%',
      height: '100%'
    }
  })))), isFullscreen && /*#__PURE__*/React.createElement("div", {
    className: "lx-btn__fullscreen-exit flex items-center"
  }, title(), /*#__PURE__*/React.createElement(_Button, {
    type: "text",
    icon: /*#__PURE__*/React.createElement(_IconFullscreenExit, null),
    onClick: () => setIsFullscreen(false)
  })), /*#__PURE__*/React.createElement("div", {
    className: "lx-flow-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center"
  }, title(), !loading && /*#__PURE__*/React.createElement(_Button, {
    className: "lx-btn__fullscreen",
    type: "text",
    icon: /*#__PURE__*/React.createElement(_IconFullscreen, null),
    onClick: () => setIsFullscreen(true)
  })), /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    style: {
      width: '100%',
      minHeight: 300,
      ...style
    }
  })));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyColors(viewer, progress, edges) {
  const modeling = viewer.get('modeling');
  const elementRegistry = viewer.get('elementRegistry');

  // 节点着色
  if (progress?.length) {
    progress.forEach(item => {
      const element = elementRegistry.get(item.id);
      if (!element) return;
      let status = item.status;
      if (item.status === 'current' && element.type === 'bpmn:EndEvent') {
        status = 'completed';
      }
      const colors = STATUS_COLORS[status];
      if (colors) {
        modeling.setColor([element], colors);
      }
    });
  }

  // 连线着色：按后端返回的 edges 精确染色，不再用节点状态去推。
  // passed → 已走（绿 / completed），active → 当前入口（蓝 / current），其他 → 还原默认。
  if (edges?.length) {
    const passed = STATUS_COLORS.completed;
    const active = STATUS_COLORS.current;
    const defaultStroke = '#000';
    edges.forEach(edge => {
      const element = elementRegistry.get(edge.id);
      if (!element) return;
      if (element.type !== 'bpmn:SequenceFlow') return;
      if (edge.status === 'passed') {
        modeling.setColor([element], {
          stroke: passed.stroke,
          fill: passed.stroke
        });
      } else if (edge.status === 'active') {
        modeling.setColor([element], {
          stroke: active.stroke,
          fill: active.stroke
        });
      } else {
        // inactive 还原默认色，避免之前被错误染成绿色时残留
        modeling.setColor([element], {
          stroke: defaultStroke,
          fill: defaultStroke
        });
      }
    });
  }
}
export default BpmnViewerComponent;